import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Globe, MapPin, ArrowRight, ClipboardCheck, LogIn, ScrollText, User } from 'lucide-react'
import SEO from '@components/SEO'
import CertificateVerifySection from '@components/CertificateVerifySection'
import { CERTIFICATION_MAX_POINTS, CERTIFICATION_PASS_POINTS } from '@lib/certificationConfig'
import { useFirebaseAuth } from '@context/FirebaseAuthContext'
import { CERTIFICATION_BTN_OUTLINE_DARK } from '@lib/certificationUi'

const tracks = [
  {
    id: 'global',
    title: 'Global & cross-border',
    description:
      'Forty thoughtful questions on multinational total rewards: culture, privacy, employment context, data ethics, change models, and public reference hubs (not legal advice).',
    to: '/certification/global',
    icon: Globe
  },
  {
    id: 'india',
    title: 'India workplace context',
    description:
      'Forty questions tuned to Indian organisations: behavioural economics, EVP, pillars, implementation, metrics, and ethics (not statutory advice; not an accredited certification).',
    to: '/certification/india',
    icon: MapPin
  }
]

export default function CertificationHubPage() {
  const { user, loading: authLoading, signOutUser } = useFirebaseAuth()

  const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.55, ease: 'easeOut' }
  }

  const passPct = Math.round((CERTIFICATION_PASS_POINTS / CERTIFICATION_MAX_POINTS) * 100)

  return (
    <div className="bg-black min-h-screen">
      <SEO
        title="Total Rewards Certification Practice — Global & India"
        description={`Practice competency assessments for total rewards professionals: Global and India tracks. Weighted scoring to ${CERTIFICATION_MAX_POINTS} points; pass at ${CERTIFICATION_PASS_POINTS} points (${passPct}%); sign in with your mobile number to save results and download certificates.`}
        keywords="total rewards certification, total rewards quiz, compensation competency, global rewards, India HR rewards"
      />

      <section className="pt-14 lg:pt-16 pb-16 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-black to-navy-900" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-3xl" />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div className="max-w-4xl mx-auto text-center mb-10" {...fadeInUp}>
            <div className="inline-flex items-center glass-effect-dark rounded-full px-5 py-2 mb-6 border border-primary-500/20">
              <ClipboardCheck className="w-4 h-4 text-primary-400 mr-2" />
              <span className="text-sm font-medium text-neutral-200">Practice assessment</span>
            </div>
            <h1 className="text-display-2 text-white mb-6 text-balance">Total Rewards competency certification</h1>
            <p className="text-body-large text-neutral-300 leading-relaxed max-w-3xl mx-auto">
              Whether you are sharpening your judgement or preparing for a stakeholder conversation, these tracks help
              you stress-test your total rewards instincts. Each path has <strong className="text-white">40 questions</strong>{' '}
              (true/false, single choice, and multi-select). This is a{' '}
              <strong className="text-white">stand-alone practice check</strong> for professional development — not an
              accredited third-party exam, and not legal advice.
            </p>
            <p className="text-sm text-neutral-400 mt-6 max-w-2xl mx-auto leading-relaxed">
              <strong className="text-white">How it works:</strong> Sign in with your mobile number (we text you a
              code). Complete one complimentary attempt per track. Pass at{' '}
              <strong className="text-white">
                {CERTIFICATION_PASS_POINTS} / {CERTIFICATION_MAX_POINTS}
              </strong>{' '}
              ({passPct}%). When you pass, download a polished PDF certificate. You can always come back to review scores
              and re-download certificates from <strong className="text-white">My progress</strong>.
            </p>
          </motion.div>

          {!authLoading && (
            <motion.div
              className="max-w-2xl mx-auto mb-12 flex flex-col sm:flex-row flex-wrap gap-3 justify-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              {user ? (
                <>
                  <Link
                    to="/certification/my-results"
                    className="btn-accent inline-flex items-center justify-center gap-2 px-5 py-3 text-sm"
                  >
                    <ScrollText className="w-4 h-4" />
                    My progress
                  </Link>
                  <button
                    type="button"
                    onClick={() => void signOutUser()}
                    className={`${CERTIFICATION_BTN_OUTLINE_DARK} gap-2 px-5 py-3 text-sm`}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <Link
                  to="/certification/sign-in"
                  className="btn-accent inline-flex items-center justify-center gap-2 px-5 py-3 text-sm"
                >
                  <LogIn className="w-4 h-4" />
                  Sign in with mobile
                </Link>
              )}
            </motion.div>
          )}

          {user?.phoneNumber ? (
            <p className="text-center text-xs text-neutral-500 mb-10 flex items-center justify-center gap-2">
              <User className="w-3.5 h-3.5 text-primary-500/80" />
              Signed in as <span className="text-neutral-300">{user.phoneNumber}</span>
            </p>
          ) : null}

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {tracks.map((track) => (
              <Link
                key={track.id}
                to={track.to}
                className="group glass-effect-dark rounded-2xl p-8 border border-primary-500/20 hover:border-primary-400/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col h-full"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                  <track.icon className="w-7 h-7 text-navy-950" />
                </div>
                <h2 className="text-display-3 text-white mb-3">{track.title}</h2>
                <p className="text-body text-neutral-400 leading-relaxed flex-1 mb-6">{track.description}</p>
                <span className="inline-flex items-center text-primary-400 font-semibold text-sm uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                  Start this track
                  <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      <CertificateVerifySection />
    </div>
  )
}
